/**
 * Completed / release log (spec page 8, approved - all four):
 *  (a) release-notes feed   (b) stats   (c) requester email - server side,
 *  functions/src/notify.ts   (d) searchable archive.
 *
 * Charts follow the dataviz method: single-series magnitude -> one hue
 * (#4a8fe0, validated on the dark surface), thin bars with rounded data-ends,
 * recessive axes, hover tooltips, a table view, no legend for one series.
 */
import { useMemo, useState } from 'react';
import { DIVISIONS, TYPES, divisionLabel, type Settings } from '../config';
import type { Req } from '../lib/data';
import { Divisions, Empty, TypeTag } from '../ui';
import { Guide } from '../guide';

const BAR = '#4a8fe0';
const DAY = 86400000;
const daysToDone = (r: Req) => r.completedAt ? Math.max(0, Math.round((r.completedAt.toMillis() - r.createdAt.toMillis()) / DAY)) : null;
const fmt = (r: Req) => r.completedAt?.toDate().toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) ?? '';

export function Completed({ items, settings, go }: { items: Req[]; settings: Settings; go: (to: string) => void }) {
  const [tab, setTab] = useState<'feed' | 'stats' | 'archive'>('feed');
  void settings;
  const done = useMemo(() => items.filter((r) => r.status === 'completed')
    .sort((a, b) => (b.completedAt?.toMillis() ?? 0) - (a.completedAt?.toMillis() ?? 0)), [items]);
  return (
    <div>
      <div className="toolbar">
        <h2 className="page-title">Completed <span className="count">{done.length}</span></h2>
        <div className="seg" style={{ margin: 0 }}>
          {(['feed', 'stats', 'archive'] as const).map((t) => (
            <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
              {t === 'feed' ? 'Release notes' : t === 'stats' ? 'Stats' : 'Search archive'}
            </button>
          ))}
        </div>
      </div>
      <Guide page="done" />
      {tab === 'feed' && <Feed done={done} go={go} />}
      {tab === 'stats' && <Stats items={items} done={done} />}
      {tab === 'archive' && <Archive done={done} go={go} />}
    </div>
  );
}

function Feed({ done, go }: { done: Req[]; go: (to: string) => void }) {
  if (!done.length) return <div className="card"><Empty>Nothing completed yet. Items land here when they reach Completed, with their release note.</Empty></div>;
  return (
    <div className="feed">
      {done.slice(0, 60).map((r) => (
        <article key={r.id} className="card release">
          <div className="release-head">
            <span className="release-date">{fmt(r)}</span>
            <h3 className="release-title" onClick={() => go(`/r/${r.id}`)}>{r.title}</h3>
          </div>
          <p className={r.releaseNote ? 'release-note' : 'muted'}>{r.releaseNote || 'No release note written.'}</p>
          <div className="release-meta">
            <Divisions ids={r.divisions} /> <TypeTag type={r.type} />
            <span className="muted small">for {r.requesterName} - {daysToDone(r)}d from request to done{r.loggedHours ? ` - ${r.loggedHours}h logged` : ''}</span>
          </div>
        </article>
      ))}
    </div>
  );
}

function Stats({ items, done }: { items: Req[]; done: Req[] }) {
  const [asTable, setAsTable] = useState(false);
  const now = Date.now();
  const last30 = done.filter((r) => r.completedAt && now - r.completedAt.toMillis() < 30 * DAY);
  const durations = done.map(daysToDone).filter((d): d is number => d != null);
  const avg = durations.length ? Math.round((durations.reduce((a, b) => a + b, 0) / durations.length) * 10) / 10 : null;
  const hours30 = last30.reduce((s, r) => s + (r.loggedHours ?? 0), 0);

  // Completed per week, last 8 weeks (oldest -> newest).
  const weeks = Array.from({ length: 8 }, (_, i) => {
    const end = now - (7 - i) * 7 * DAY;
    const start = end - 7 * DAY;
    const count = done.filter((r) => r.completedAt && r.completedAt.toMillis() > start && r.completedAt.toMillis() <= end).length;
    return { label: new Date(end).toLocaleDateString([], { month: 'short', day: 'numeric' }), value: count };
  });

  // Logged hours by division (all requests). A request tagged to several divisions splits its hours evenly.
  const byDiv = new Map<string, number>();
  for (const r of items) {
    if (!r.loggedHours) continue;
    for (const d of r.divisions) byDiv.set(d, (byDiv.get(d) ?? 0) + r.loggedHours / r.divisions.length);
  }
  const divRows = [...byDiv.entries()].map(([d, h]) => ({ label: divisionLabel(d), value: Math.round(h * 10) / 10 }))
    .sort((a, b) => b.value - a.value);

  return (
    <div>
      <div className="stat-tiles">
        <div className="stat"><span className="stat-num">{last30.length}</span><span className="stat-label">completed in the last 30 days</span></div>
        <div className="stat"><span className="stat-num">{avg ?? '-'}{avg != null && <small>d</small>}</span><span className="stat-label">average from request to done</span></div>
        <div className="stat"><span className="stat-num">{Math.round(hours30 * 10) / 10}<small>h</small></span><span className="stat-label">logged on work completed in 30 days</span></div>
      </div>
      <div className="toolbar"><span className="spacer" /><button className="linkish" onClick={() => setAsTable((v) => !v)}>{asTable ? 'Show charts' : 'Show as table'}</button></div>
      <div className="stat-charts">
        <section className="card">
          <h2>Completed per week</h2>
          {asTable ? <DataTable rows={weeks} unit="" head={['Week ending', 'Completed']} /> : <ColumnChart rows={weeks} unit="" />}
        </section>
        <section className="card">
          <h2>Hours logged by division</h2>
          {!divRows.length ? <Empty>No hours logged yet. Log hours from the Planner or a request's detail page.</Empty>
            : asTable ? <DataTable rows={divRows} unit="h" head={['Division', 'Hours']} /> : <BarChart rows={divRows} unit="h" />}
        </section>
      </div>
    </div>
  );
}

interface Row { label: string; value: number }

function DataTable({ rows, unit, head }: { rows: Row[]; unit: string; head: [string, string] }) {
  return (
    <table className="list">
      <thead><tr><th>{head[0]}</th><th>{head[1]}</th></tr></thead>
      <tbody>{rows.map((r) => <tr key={r.label}><td>{r.label}</td><td className="num">{r.value}{unit}</td></tr>)}</tbody>
    </table>
  );
}

/** Vertical columns over time. Bars rounded at the data end only, anchored to the baseline. */
function ColumnChart({ rows, unit }: { rows: Row[]; unit: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 520, H = 180, padL = 28, padB = 24, padT = 12;
  const max = Math.max(1, ...rows.map((r) => r.value));
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1];
  const slot = (W - padL) / rows.length;
  const bw = Math.min(28, slot * 0.55);
  const y = (v: number) => padT + (H - padT - padB) * (1 - v / top);
  return (
    <div className="chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="chart" role="img" aria-label="Completed requests per week, last 8 weeks">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W} y1={y(t)} y2={y(t)} className="grid" />
            <text x={padL - 6} y={y(t) + 3} className="axis" textAnchor="end">{t}</text>
          </g>
        ))}
        {rows.map((r, i) => {
          const cx = padL + slot * i + slot / 2;
          const h = y(0) - y(r.value);
          return (
            <g key={r.label} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect x={padL + slot * i} y={padT} width={slot} height={H - padT - padB} fill="transparent" />
              {r.value > 0 && <path d={roundedTop(cx - bw / 2, y(r.value), bw, h, Math.min(4, h))} fill={BAR} opacity={hover == null || hover === i ? 1 : 0.55} />}
              <text x={cx} y={H - 8} className="axis" textAnchor="middle">{r.label}</text>
            </g>
          );
        })}
      </svg>
      {hover != null && <div className="tip" style={{ left: `${((padL + (W - padL) / rows.length * (hover + 0.5)) / W) * 100}%` }}>
        <b>{rows[hover].value}{unit}</b> completed<br /><span>week ending {rows[hover].label}</span></div>}
    </div>
  );
}

/** Horizontal bars by category, sorted, value labelled at the bar end in text ink. */
function BarChart({ rows, unit }: { rows: Row[]; unit: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 520, rowH = 26, padL = 120, padR = 48;
  const H = rows.length * rowH + 8;
  const max = Math.max(1, ...rows.map((r) => r.value));
  const x = (v: number) => ((W - padL - padR) * v) / max;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="chart" role="img" aria-label="Hours logged by division">
      {rows.map((r, i) => {
        const yy = 4 + i * rowH;
        const w = x(r.value);
        return (
          <g key={r.label} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            <rect x={0} y={yy} width={W} height={rowH} fill="transparent" />
            <text x={padL - 8} y={yy + rowH / 2 + 4} className="axis-label" textAnchor="end">{r.label}</text>
            {w > 0 && <path d={roundedRight(padL, yy + 6, w, rowH - 12, Math.min(4, w))} fill={BAR} opacity={hover == null || hover === i ? 1 : 0.55} />}
            <text x={padL + w + 6} y={yy + rowH / 2 + 4} className="value">{r.value}{unit}</text>
          </g>
        );
      })}
    </svg>
  );
}

function niceTicks(max: number): number[] {
  const step = max <= 5 ? 1 : max <= 10 ? 2 : max <= 25 ? 5 : Math.ceil(max / 5 / 5) * 5;
  const out = [];
  for (let t = 0; t <= max + step - 1; t += step) { out.push(t); if (t >= max) break; }
  return out;
}
function roundedTop(x: number, y: number, w: number, h: number, r: number) {
  return `M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h} Z`;
}
function roundedRight(x: number, y: number, w: number, h: number, r: number) {
  return `M${x},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h - r} Q${x + w},${y + h} ${x + w - r},${y + h} H${x} Z`;
}

function Archive({ done, go }: { done: Req[]; go: (to: string) => void }) {
  const [q, setQ] = useState('');
  const [div, setDiv] = useState('');
  const [type, setType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [who, setWho] = useState('');
  const people = [...new Set(done.map((r) => r.requesterName))].sort();
  const rows = done.filter((r) => {
    const t = r.completedAt?.toDate();
    const iso = t ? `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}` : '';
    return (!q || `${r.title} ${r.releaseNote ?? ''} ${r.description ?? ''}`.toLowerCase().includes(q.toLowerCase()))
      && (!div || r.divisions.includes(div as never))
      && (!type || r.type === type)
      && (!who || r.requesterName === who)
      && (!from || iso >= from) && (!to || iso <= to);
  });
  return (
    <div className="card">
      <div className="toolbar">
        <input className="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles and release notes..." />
        <select value={div} onChange={(e) => setDiv(e.target.value)}>
          <option value="">All divisions</option>
          {DIVISIONS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All types</option>
          {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <select value={who} onChange={(e) => setWho(e.target.value)}>
          <option value="">Anyone</option>
          {people.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <label className="muted small">from <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="muted small">to <input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <span className="count">{rows.length}</span>
      </div>
      {!rows.length && <Empty>No completed requests match.</Empty>}
      <table className="list">
        <thead><tr><th>Completed</th><th>Request</th><th>Divisions</th><th>Asked by</th><th>Days</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} onClick={() => go(`/r/${r.id}`)}>
              <td className="num">{fmt(r)}</td>
              <td className="t">{r.title}{r.releaseNote && <div className="muted small">{r.releaseNote.slice(0, 140)}</div>}</td>
              <td><Divisions ids={r.divisions} /></td>
              <td>{r.requesterName}</td>
              <td className="num">{daysToDone(r)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
